import { useEffect, useState } from 'react';
import ExtensionDownloadModal from './ExtensionDownloadModal';
import ConsumerExtensionModal from './ConsumerExtensionModal';

interface ExtensionSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ExtensionSelectionModal({ isOpen, onClose }: ExtensionSelectionModalProps) {
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showConsumerModal, setShowConsumerModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBusinessClick = () => {
    window.open('https://chromewebstore.google.com/detail/biomail-xeroverifier-for/dijojimboooldcgkeghjkmhgdpaealda', '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleConsumerClick = () => {
    window.open('https://chromewebstore.google.com/detail/biomail-bioverifier/jgbpooaoafoefkplcfkeiolociiigjhk', '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleBusinessModalClose = () => {
    setShowBusinessModal(false);
    onClose();
  };

  const handleConsumerModalClose = () => {
    setShowConsumerModal(false);
    onClose();
  };

  return (
    <>
      {!showBusinessModal && !showConsumerModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
          onClick={onClose}
        >
          <div 
            className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-4xl w-full"
            style={{ maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-8 py-6 relative"
              style={{ backgroundColor: '#29B3E6' }}
            >
              <h2 className="text-3xl font-bold text-white">
                Choose Extension Type
              </h2>
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white transition-colors cursor-pointer w-8 h-8 flex items-center justify-center rounded"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="p-8">
              <p className="text-lg text-gray-600 text-center mb-8">
                Select the type of extension you want to install
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Business Extension Card */}
                <div 
                  className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={handleBusinessClick}
                >
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                      <i className="ri-building-line text-5xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Extension</h3>
                    <p className="text-gray-600 mb-6">
                      For businesses using QuickBooks Online or Xero. Add verification stamps to your invoices and protect your business.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                      <i className="ri-download-line text-xl"></i>
                      <span className="font-semibold">Install Business Extension</span>
                    </div>
                  </div>
                </div>

                {/* Consumer Extension Card */}
                <div 
                  className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={handleConsumerClick}
                >
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                      <i className="ri-user-line text-5xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Consumer Extension</h3>
                    <p className="text-gray-600 mb-6">
                      For consumers who receive invoices. Verify invoice authenticity with a single click and protect yourself from fraud.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                      <i className="ri-download-line text-xl"></i>
                      <span className="font-semibold">Install Consumer Extension</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-information-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Not Sure Which One?</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>Business Extension:</strong> If you send invoices to customers</li>
                      <li>• <strong>Consumer Extension:</strong> If you receive invoices from businesses</li>
                      <li>• You can install both extensions if needed</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Business Extension Modal */}
      <ExtensionDownloadModal 
        isOpen={showBusinessModal}
        onClose={handleBusinessModalClose}
      />

      {/* Consumer Extension Modal */}
      <ConsumerExtensionModal 
        isOpen={showConsumerModal}
        onClose={handleConsumerModalClose}
      />
    </>
  );
}